declare global {
  interface Window {
    fbq: (method: string, event: string, params?: Record<string, unknown>, options?: { eventID?: string }) => void;
    gtag: (command: string, event: string, params?: Record<string, unknown>) => void;
    clarity: (method: string, event: string) => void;
  }
}

export function trackEvent(eventName: string, params: Record<string, unknown> = {}, eventID?: string): void {
  if (typeof window.fbq === 'function') {
    // eventID (opcional): permite deduplicar con el mismo evento enviado por CAPI.
    window.fbq('track', eventName, params, eventID ? { eventID } : undefined);
  }
  if (typeof window.gtag === 'function') {
    window.gtag('event', eventName, params);
  }
  if (typeof window.clarity === 'function') {
    window.clarity('event', eventName);
  }
}

export function trackCustomEvent(eventName: string, params: Record<string, unknown> = {}): void {
  if (typeof window.fbq === 'function') window.fbq('trackCustom', eventName, params);
  if (typeof window.gtag === 'function') window.gtag('event', eventName, params);
}

// Embudo orgánico → mc-events (funnel hit_mentor) vía /ev/hit de mc-funnel.
// Mide visita → aplicacion → agendo con las UTMs del enlace (persistidas por
// useUtmTracking en localStorage 'hit_utm_data'). 100% FAIL-OPEN: fire-and-forget,
// jamás rompe ni demora la página.
const MC_EV_URL = 'https://mc-funnel.marketing-302.workers.dev/ev/hit';
const HIT_UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const;

function hitSessionId(): string {
  try {
    let s = sessionStorage.getItem('hit_sid');
    if (!s) {
      s = typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : String(Date.now()) + Math.random().toString(36).slice(2);
      sessionStorage.setItem('hit_sid', s);
    }
    return s;
  } catch {
    return '';
  }
}

// Variante ?v= y carril ?p= del VSL era-mentor, persistidos por useUtmTracking
// en localStorage 'hit_carril_data'. Se estampan en el `pag` (→ variante de
// mc-events) para que las etapas que DECIDEN (aplicacion/agendo) se puedan
// leer POR variante del VSL y POR PAÍS (páginas de España y USA hispano,
// blueprint hit-era-mentor.md). Saneados al alfabeto que acepta /ev/hit.
function carrilDatos(): { v: string; pais: string } {
  try {
    const c = JSON.parse(localStorage.getItem('hit_carril_data') || '{}') as Record<string, string>;
    const v = typeof c.v === 'string'
      ? c.v.toLowerCase().replace(/[^a-z0-9-]/g, '').replace(/^-+/, '').slice(0, 12)
      : '';
    // p llega como 'era-mentor-es' / 'era-mentor-usa' (VSL por país) o
    // 'era-mentor' (genérico). Solo el sufijo de país pasa al pag.
    const p = typeof c.p === 'string' ? c.p : '';
    const pais = p === 'era-mentor-es' ? 'es' : p === 'era-mentor-usa' ? 'usa' : '';
    return { v, pais };
  } catch {
    return { v: '', pais: '' };
  }
}

export function sendHitFunnel(etapa: 'visita' | 'aplicacion' | 'agendo'): void {
  try {
    let utm: Record<string, string> = {};
    try {
      utm = JSON.parse(localStorage.getItem('hit_utm_data') || '{}') as Record<string, string>;
    } catch {
      /* sin UTMs guardadas: el evento igual cuenta (sin origen) */
    }
    // pag → variante 'lmai-era-mentor-app[-<pais>][-<v>]' en mc-events:
    // distingue este carril de la SPA original (que emite 'lmai-aplicacion')
    // y separa España/USA. Máx 32 chars en /ev/hit:
    // 'era-mentor-app-usa-' (19) + v (≤12) = 31, sin truncado.
    const { v, pais } = carrilDatos();
    const payload: Record<string, unknown> = {
      etapa,
      pag: `era-mentor-app${pais ? `-${pais}` : ''}${v ? `-${v}` : ''}`,
      session_id: hitSessionId(),
      page_url: window.location.href,
    };
    HIT_UTM_KEYS.forEach((k) => {
      if (utm[k]) payload[k] = utm[k];
    });
    const blob = new Blob([JSON.stringify(payload)], { type: 'text/plain' });
    if (!(navigator.sendBeacon && navigator.sendBeacon(MC_EV_URL, blob))) {
      void fetch(MC_EV_URL, { method: 'POST', body: JSON.stringify(payload), keepalive: true }).catch(() => {});
    }
  } catch {
    /* fail-open */
  }
}

export function getCookie(name: string): string {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(';').shift() ?? '';
  return '';
}

export function getGAClientId(): string {
  const gaCookie = getCookie('_ga');
  if (!gaCookie) return '';
  const parts = gaCookie.split('.');
  if (parts.length >= 4) return `${parts[2]}.${parts[3]}`;
  return gaCookie;
}
